import { reactive, shallowRef } from 'vue'

/**
 * Confirmación global.
 *
 * Antes: doce páginas repetían el mismo bloque de siete líneas para instanciar
 * <ConfirmDialog>, y otras seis se montaban su propio v-dialog de confirmar a
 * mano (con anchos, colores y botones distintos). Ahora hay un único host en el
 * layout y las páginas solo preguntan.
 *
 *   const ok = await confirm({ title, message })
 *   if (!ok) return
 *
 * Si la acción tarda y se quiere mantener el diálogo con el botón en carga
 * mientras se ejecuta, se pasa `onConfirm`:
 *
 *   await confirm({ title, message, onConfirm: () => borrar(id) })
 *
 * `onConfirm` debe gestionar sus propios errores: si lanza, el diálogo se
 * cierra y la promesa se rechaza.
 */

const DEFAULTS = {
  title: '',
  message: '',
  confirmLabel: '',
  confirmColor: 'error',
  confirmIcon: 'mdi-delete',
}

export const confirmState = reactive({
  show: false,
  pending: false,
  ...DEFAULTS,
})

const pending = shallowRef(null) // { resolve, reject, onConfirm }

function settle(accepted, error) {
  const current = pending.value
  pending.value = null
  confirmState.show = false
  confirmState.pending = false
  if (!current) return
  if (error) current.reject(error)
  else current.resolve(accepted)
}

/** Abre el diálogo. Devuelve una promesa que resuelve a true (aceptar) o false. */
export function confirm(options = {}) {
  // Nunca dos a la vez: si había uno abierto, se resuelve como cancelado.
  settle(false)

  const { onConfirm, ...dialogProps } = options
  Object.assign(confirmState, DEFAULTS, dialogProps, { show: true, pending: false })

  return new Promise((resolve, reject) => {
    pending.value = { resolve, reject, onConfirm: onConfirm || null }
  })
}

export function cancelConfirm() {
  if (confirmState.pending) return
  settle(false)
}

export async function acceptConfirm() {
  const current = pending.value
  if (!current) return

  if (!current.onConfirm) {
    settle(true)
    return
  }

  confirmState.pending = true
  try {
    await current.onConfirm()
    settle(true)
  } catch (error) {
    settle(false, error)
  }
}

export function useConfirm() {
  return { confirm, confirmState, cancelConfirm, acceptConfirm }
}
